import React from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { keyframes } from '@mui/system';
import { SUCCESS, SUCCESS_BG, SUCCESS_DARK, INFO, INFO_BG, INFO_DARK, TEXT_SLATE, TEXT_LIGHT_SLATE, OVERLAY_DARK } from '../../utilities/colors';

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0) scale(1);
  }
  40% {
    transform: translateY(-20px) scale(1.1);
  }
  60% {
    transform: translateY(-10px) scale(1.05);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const SubmissionModal = ({ open, onClose, totalQuestions, answeredQuestions }) => {
  const unansweredCount = totalQuestions - answeredQuestions;
  const allAnswered = unansweredCount === 0;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 0,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          fontFamily: 'Manrope, sans-serif',
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: OVERLAY_DARK,
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <Box
        sx={{
          height: 4,
          background: allAnswered
            ? `linear-gradient(90deg, #22c55e 0%, ${SUCCESS} 100%)`
            : `linear-gradient(90deg, #3b82f6 0%, ${INFO} 100%)`,
        }}
      />

      <DialogContent sx={{ p: 3, textAlign: 'center' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 2,
            animation: allAnswered 
              ? `${bounceAnimation} 0.8s ease-out`
              : `${pulseAnimation} 2s ease-in-out infinite`,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: allAnswered
                ? `linear-gradient(135deg, ${SUCCESS_BG} 0%, #bbf7d0 100%)`
                : `linear-gradient(135deg, ${INFO_BG} 0%, #bfdbfe 100%)`,
              boxShadow: allAnswered
                ? '0 8px 24px rgba(34, 197, 94, 0.25)'
                : '0 8px 24px rgba(59, 130, 246, 0.25)',
            }}
          >
            {allAnswered ? (
              <CheckCircleIcon
                sx={{
                  fontSize: 50,
                  color: SUCCESS,
                }}
              />
            ) : (
              <AccessTimeIcon
                sx={{
                  fontSize: 50,
                  color: INFO,
                }}
              />
            )}
          </Box>
        </Box>

        <Typography 
          variant="h5" 
          component="div" 
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 800,
            mb: 2,
            color: allAnswered ? SUCCESS : INFO,
          }}
        >
          {allAnswered ? 'All Done!' : 'Quick Reminder'}
        </Typography>

        {allAnswered ? (
          <Typography 
            variant="body1" 
            sx={{ 
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              color: TEXT_SLATE,
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            Your predictions are submitted! Come back after the match to see your score.
          </Typography>
        ) : (
          <>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
                color: TEXT_SLATE,
                mb: 1,
                lineHeight: 1.6,
              }}
            >
              You have{' '}
              <span style={{ fontWeight: 800, color: INFO }}>
                {unansweredCount}
              </span>{' '}
              more question{unansweredCount !== 1 ? 's' : ''} to fill
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
                color: TEXT_LIGHT_SLATE,
                mb: 3,
              }}
            >
              Complete them before time runs out!
            </Typography>
          </>
        )}

        <Button 
          onClick={onClose} 
          variant="contained" 
          size="large"
          fullWidth
          sx={{ 
            py: 1.5,
            fontFamily: 'Manrope, sans-serif',
            fontSize: '0.95rem',
            fontWeight: 700,
            borderRadius: 2,
            background: allAnswered
              ? `linear-gradient(135deg, #22c55e 0%, ${SUCCESS} 100%)`
              : `linear-gradient(135deg, #3b82f6 0%, ${INFO} 100%)`,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: allAnswered
              ? '0 8px 20px rgba(34, 197, 94, 0.3)'
              : '0 8px 20px rgba(59, 130, 246, 0.3)',
            border: 'none',
            '&:hover': {
              background: allAnswered
                ? `linear-gradient(135deg, ${SUCCESS} 0%, ${SUCCESS_DARK} 100%)`
                : `linear-gradient(135deg, ${INFO} 0%, ${INFO_DARK} 100%)`,
              boxShadow: allAnswered
                ? '0 12px 28px rgba(34, 197, 94, 0.4)'
                : '0 12px 28px rgba(59, 130, 246, 0.4)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease',
            },
            '&:active': {
              transform: 'translateY(0)',
            }
          }}
        >
          {allAnswered ? 'Continue' : 'Got It'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionModal;